import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Icons } from "assets/icons";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import {
  getUsers,
  resendInvite,
  resetFilters,
  setFilter,
  updateUserStatus,
} from "store/slices/usersSlice";
import FilterBar from "components/common/FilterBar";
import { Dropdown, Modal, Table } from "react-bootstrap";
import Switch from "components/common/Switch";
import { hideDeleteModal, showDeleteModal } from "store/slices/modalSlice";
import { MpinStatusBadge, StatusBadge, UserStatusBadge } from "components/common/StatusBadge";
import TableShimmer from "components/common/TableShimmer";
import NoDataFound from "components/common/NoDataFound";
import { handleStatusUpdate, handleSubmitForm } from "helpers/apiUtils";
import { ADMIN_ACCOUNT_TYPE, ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";
import { Ellipsis } from "lucide-react";
import UpdateMpin from "./UpdateMpin";
import ChangePassword from "./ChangePassword";

const Users = () => {
  const { t } = useTranslation();
  const { trash, edit, noUsers, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("users");

  const {
    items: users,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, accountType },
  } = useSelector((state) => state.Users);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedOptionType, setSelectedOptionType] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      accountType: isReset ? "" : accountType,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getUsers(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "accountType",
      placeholder: t("Account Type"),
      type: "singleSelect",
      options: ADMIN_ACCOUNT_TYPE,
    },
    // {
    //   key: "role",
    //   placeholder: t("Select Roles"),
    //   type: "multiSelect",
    //   options: [
    //     { name: "Maintainer", value: -1 },
    //     { name: "Admin", value: 1 },
    //     { name: "Sub Admin", value: 2 },
    //     { name: "Branch", value: 3 },
    //   ],
    // },
  ];

  const handleResendInvitation = (id) => {
    setSelectedId(id);
    handleSubmitForm({
      dispatch,
      action: resendInvite,
      payload: id,
    });
  };

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
        render: (value) => value || "-",
      },
      {
        header: t("Email"),
        key: "email",
        render: (value) => value || "-",
      },
      {
        header: t("Phone Number"),
        key: "phoneNumber",
        render: (value) => <p className="ltr_text">{value || "-"}</p>,
      },
      {
        header: t("Role"),
        key: "roleId",
        render: (value) => value?.name || "-",
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("User Type"),
        key: "accountType",
        render: (value) => value,
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <UserStatusBadge status={value} t={t} />,
      },
      {
        header: t("Mpin Status"),
        key: "mpin",
        render: (value) => <MpinStatusBadge status={value?.isSet || null} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            {item.status === -1 && (
              <button
                className="main_btn outline small"
                onClick={() => handleResendInvitation(item._id)}
                disabled={isLoading && selectedId === item._id}
              >
                {t("Resend Invite")}
              </button>
            )}
            {item.status !== -1 && (
              <Switch
                checked={item.status === 1}
                handleOnChange={() => {
                  handleStatusUpdate({
                    _id: item._id,
                    status: item.status,
                    dispatch,
                    action: updateUserStatus,
                  });
                }}
                disabled={isLoading && loadingItemId === item._id}
              />
            )}
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() => navigate(`/users/edit-user?id=${item._id}`)}
            />
          </>
        )}
        {canView && (
          <Link to={`/users/user-details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
        )}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt="delete"
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete User"),
                  message: t("Are you sure you want to delete this User?"),
                  itemId: item._id,
                  actionType: "USER",
                }),
              );
            }}
          />
        )}
        {canEdit && (
          <Dropdown className="info_dropdown">
            <Dropdown.Toggle>
              <Ellipsis className="text_black" />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item
                href="#"
                onClick={() => {
                  setShowOptionsModal(true);
                  setSelectedOptionType("password");
                  setUpdatingUserId(item._id);
                }}
              >
                {t("Change Password")}
              </Dropdown.Item>
              {item.accountType === "staff" && (
                <Dropdown.Item
                  href="#"
                  onClick={() => {
                    setShowOptionsModal(true);
                    setSelectedOptionType("mpin");
                    setUpdatingUserId(item._id);
                  }}
                >
                  {item?.mpin?.isSet ? t("Update Mpin") : t("Set Mpin")}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, selectedId, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content users">
      <h4 className="page_title mb-4">{t("Users")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={"Search by Name, Email, Number"}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy ,accountType }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add User")}
        onBtnClick={() => navigate("/users/add-user")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && users?.length === 0 && (
        <NoDataFound title={t("No users found")} icon={noUsers} />
      )}

      {!loading && users?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={users}
            renderActions={renderActions}
            t={t}
          />
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
      <Modal
        show={showOptionsModal}
        centered
        onHide={() => setShowOptionsModal(false)}
        className="add_update_modal p-0"
      >
        <div className="inner_section">
          {selectedOptionType === "mpin" ? (
            <UpdateMpin
              updatingUserId={updatingUserId}
              onSuccess={() => {
                setShowOptionsModal(false);
                getAllData();
              }}
              setShowOptionsModal={setShowOptionsModal}
            />
          ) : (
            <ChangePassword
              updatingUserId={updatingUserId}
              onSuccess={() => {
                setShowOptionsModal(false);
                getAllData();
              }}
              setShowOptionsModal={setShowOptionsModal}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Users;
